import { yupResolver } from '@hookform/resolvers/yup'
import * as Sentry from '@sentry/nextjs'
import { DSText, DSTextAndIcon, DSButton, DSSpacer } from '@zoe/ds-web'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Form } from 'react-aria-components'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import * as Yup from 'yup'

import type { FragmentComponent } from '@/types/graphql'

import { GeoContext } from '@/context/geoContext'
import { TrackingContext } from '@/context/trackingContext'

import { DSTextInput } from '@/components/ds/TextInput/TextInput'

import { EMAIL_CAPTURE_CMS_EMAIL_SUBMITTED, MarketingConsentType } from '@/lib/braze/brazeConstants'
import { registerBrazeUserWithEmail } from '@/lib/braze/brazeUtils'
import { trackSignupEvent } from '@/lib/gtm/leadGtm'
import { PHONE_COUNTRY_CODES } from '@/lib/utils/phoneCountryCodes'

import { processTypographyMarkdown } from './Typography'

export type EmailCaptureRecord = {
  __typename: 'EmailCaptureRecord'
  id: string
  withNameField: boolean
  withPhoneField: boolean
  placementId: string
  nameLabel?: string
  emailLabel?: string
  phoneLabel?: string
  namePlaceholder?: string
  emailPlaceholder?: string
  phonePlaceholder?: string
  ctaLabel?: string
  footer?: string
  brazeEventSource?: string
  signupSuccessMessage?: string
  signupErrorMessage?: string
  ctaColorScheme?: 'default' | 'inverse' | 'neutral'
}

const fragment = `
  fragment EmailCaptureBlockFragment on EmailCaptureRecord {
    __typename
    id
    withNameField
    withPhoneField
    phoneLabel
    phonePlaceholder
    namePlaceholder
    emailPlaceholder
    ctaLabel
    ctaColorScheme
    footer
    signupSuccessMessage
    signupErrorMessage
    brazeEventSource
    placementId
  }
`

interface EmailFormValues {
  name?: string
  email: string
  phone?: string
}

const StyledPhoneContainer = styled.div`
  display: flex;
  gap: var(--grid-8);
`

const StyledCountryCodeWrapper = styled.div`
  position: relative;
  width: 80px;
  overflow: hidden;
`

const StyledPrefixDisplay = styled.div<{ $isFocused?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: var(--padding-input-y) var(--padding-input-x);
  padding-right: calc(var(--padding-input-x) + 24px);
  pointer-events: none;
  color: var(--color-text-input);
  font-family: var(--font-sans-serif-light);
  font-size: var(--font-size-input);
  line-height: var(--line-height-input);
  z-index: 1;
  background-color: var(--color-surface-input);
  border: 1px solid
    ${({ $isFocused }) => ($isFocused ? 'var(--color-border-input-focus)' : 'var(--color-border-input)')};
  border-radius: var(--radius-8);
  overflow: hidden;
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" fill="none"%3E%3Cpath d="M2.33594 6.05664L7.78066 11.5014C7.89782 11.6185 8.08777 11.6185 8.20492 11.5014L13.6496 6.05664" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" /%3E%3C/svg%3E');
  transition: border 0.3s ease;
`

const StyledCountryCodeSelect = styled.select`
  background-color: transparent;
  border: 0px;
  border-radius: var(--radius-8);
  color: transparent;
  font-family: var(--font-sans-serif-light);
  font-size: var(--font-size-input);
  line-height: var(--line-height-input);
  outline: none;
  padding: var(--padding-input-y) var(--padding-input-x);
  padding-right: calc(var(--padding-input-x) + 24px);
  transition: border 0.3s ease;
  width: 250px;
  appearance: none;
  cursor: pointer;
  z-index: 2;
  position: relative;
  option {
    color: var(--color-text-input);
    background-color: var(--color-surface-input);
  }
`

const StyledPhoneInput = styled.div`
  flex: 1;
`

export const EmailCaptureBlock: FragmentComponent<{}, EmailCaptureRecord> = ({ record }) => {
  const [submitSuccess, setSubmitSuccess] = useState<boolean | undefined>(undefined)
  const [isCountryCodeFocused, setIsCountryCodeFocused] = useState(false)
  const [phoneCountryCode, setPhoneCountryCode] = useState<string>('')
  const { country } = useContext(GeoContext)
  const { section } = useContext(TrackingContext)

  const emailFormSchema = Yup.object({
    email: Yup.string().email('Email address does not look right').required('Email address is required'),
  })
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<EmailFormValues>({
    resolver: yupResolver(emailFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  })

  useEffect(() => {
    setPhoneCountryCode(country || 'GB')
  }, [country])

  const handleCountryCodeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setPhoneCountryCode(e.target.value)
  }, [])

  const handleCountryCodeBlur = useCallback(() => {
    setIsCountryCodeFocused(false)
  }, [])

  const handleCountryCodeFocus = useCallback(() => {
    setIsCountryCodeFocused(true)
  }, [])

  const onSubmit = async (data: EmailFormValues) => {
    try {
      await registerBrazeUserWithEmail(
        data.email,
        EMAIL_CAPTURE_CMS_EMAIL_SUBMITTED,
        true,
        {
          firstName: data.name,
          country: phoneCountryCode || country,
          phone: data.phone
            ? `${PHONE_COUNTRY_CODES.find((c) => c.code === phoneCountryCode)?.prefix || ''}${data.phone}`
            : undefined,
        },
        {
          source: record.brazeEventSource ?? 'email_capture_block',
          smsMarketingConsent: data.phone ? MarketingConsentType.OPTIN : MarketingConsentType.NOCHANGE,
        },
      )
      trackSignupEvent({
        email: data.email,
        section:
          section || record.placementId
            ? `${section ? section : ''}${record.placementId ? (section ? '-' : '') + record.placementId : ''}`
            : 'unspecified',
        emailList: record.brazeEventSource ?? 'email_capture_block',
        has_provided_phone_number: !!data.phone,
      })
      setSubmitSuccess(true)
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          brazeEventSource: record.brazeEventSource ?? 'email_capture_block',
          ...(section && { section }),
          ...(record.placementId && { placementId: record.placementId }),
        },
        extra: {
          context: 'Error registering user with Braze',
        },
      })
      setSubmitSuccess(false)
    }
  }
  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {record.withNameField && (
        <>
          <DSTextInput
            name="name"
            control={control}
            label={record.nameLabel}
            placeholder={record.namePlaceholder || 'Your name'}
            type="text"
            isRequired
            testId="name"
          />
          <DSSpacer size={12} direction="vertical" />
        </>
      )}
      <DSTextInput
        name="email"
        control={control}
        label={record.emailLabel}
        placeholder={record.emailPlaceholder || 'Your email address'}
        type="email"
        isRequired
        testId="email"
      />
      <DSSpacer size={16} direction="vertical" />
      {record.withPhoneField && (
        <>
          {record.phoneLabel && (
            <>
              <DSText variant="fluid-label-100" weight="regular" color="primary">
                {record.phoneLabel}
              </DSText>
              <DSSpacer size={8} direction="vertical" />
            </>
          )}
          <StyledPhoneContainer>
            <StyledCountryCodeWrapper>
              <StyledPrefixDisplay $isFocused={isCountryCodeFocused}>
                {PHONE_COUNTRY_CODES.find((c) => c.code === phoneCountryCode)?.prefix || '+44'}
              </StyledPrefixDisplay>
              <StyledCountryCodeSelect
                id="phoneCountryCode"
                name="phoneCountryCode"
                value={phoneCountryCode || 'GB'}
                onChange={handleCountryCodeChange}
                onBlur={handleCountryCodeBlur}
                onFocus={handleCountryCodeFocus}
                aria-labelledby="phoneCountryCode"
              >
                {PHONE_COUNTRY_CODES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.prefix} {country.name}
                  </option>
                ))}
              </StyledCountryCodeSelect>
            </StyledCountryCodeWrapper>
            <StyledPhoneInput>
              <DSTextInput
                name="phone"
                control={control}
                label=""
                placeholder={record.phonePlaceholder || 'Your phone number'}
                type="tel"
                testId="phone"
              />
            </StyledPhoneInput>
          </StyledPhoneContainer>
          <DSSpacer size={12} direction="vertical" />
        </>
      )}
      <DSButton
        type="submit"
        isDisabled={isSubmitting || !isValid || submitSuccess === true}
        variant="primary"
        size="large"
        width="full"
        colorScheme={record.ctaColorScheme || 'default'}
        testId="email-form-button"
      >
        <DSText
          variant="fluid-paragraph-200"
          weight="semiBold"
          color={record.ctaColorScheme === 'inverse' ? 'primary-inverse' : 'primary'}
        >
          {record.ctaLabel || 'Submit'}
        </DSText>
      </DSButton>

      <DSSpacer size={16} direction="vertical" />
      {record.footer && (
        <DSText variant="fluid-label-100" weight="regular" color="primary">
          {processTypographyMarkdown({
            content: record.footer,
            variant: `fluid-label-100`,
            weight: 'regular',
          })}
        </DSText>
      )}
      {submitSuccess === true && (
        <>
          <DSSpacer size={16} direction="vertical" />
          <DSTextAndIcon
            icon={{ name: 'tick-in-star', size: 24, position: 'left', color: 'brand' }}
            variant="fluid-paragraph-300"
            weight="regular"
          >
            {record.signupSuccessMessage || 'Thank you for signing up!'}
          </DSTextAndIcon>
        </>
      )}
      {submitSuccess === false && (
        <>
          <DSSpacer size={16} direction="vertical" />
          <DSTextAndIcon
            icon={{ name: 'close', size: 16, position: 'left', color: 'error' }}
            variant="fluid-paragraph-200"
            weight="regular"
            color="error"
          >
            {record.signupErrorMessage || 'There was an error. Please try again.'}
          </DSTextAndIcon>
        </>
      )}
    </Form>
  )
}

EmailCaptureBlock.fragment = fragment
EmailCaptureBlock.recordName = 'EmailCaptureRecord'
